import React from 'react';
import { GenericStore } from '@/rerun-manager/GenericStore';
import { apiService } from '../api-functions/api-service';
import { documentStore } from './documentStore';
import { objectsStore } from "./objectsStore";
import { templatesStore } from "./templatesStore";
import { fetchUserContext } from './unifiedUserStore';

export interface Folder {
  id: string;
  name: string;
  color?: string; // Hex color for folder customization
  subfolder?: Folder[];
  children?: Folder[]; // For compatibility with UI component
  itemCount?: number;
}

export interface GameFolderSection {
  type: 'documents' | 'templates' | 'library' | 'dialogs' | 'flows' | 'variables';
  folders: Folder[];
}

export interface GameFolders {
  gameId: string;
  folders: GameFolderSection[];
}

export interface FolderCreateRequest {
  name: string;
  parentId?: string;
  gameId: string;
  itemType: 'documents' | 'templates' | 'library' | 'dialogs' | 'flows' | 'variables';
}

export interface FolderUpdateRequest {
  id: string;
  name?: string;
  color?: string;
  parentId?: string;
  gameId: string;
  itemType: 'documents' | 'templates' | 'library' | 'dialogs' | 'flows' | 'variables';
}

// Create the folder store using GenericStore for game folders
const gameFoldersStore = new GenericStore<GameFolders>({ key: 'game-folders' });

// Helper function to convert subfolder structure to children structure for UI
function convertToUIFolders(folders: Folder[]): Folder[] {
  return folders.map(folder => ({
    ...folder,
    children: folder.subfolder ? convertToUIFolders(folder.subfolder) : []
  }));
}

// Helper function to recursively collect all folder IDs from a parent folder and its subfolders
function collectAllSubfolderIds(folderId: string, allFolders: Folder[]): string[] {
  const folderIds = [folderId]; // Include the parent folder itself

  // Find the parent folder
  const findFolderRecursively = (folders: Folder[], targetId: string): Folder | null => {
    for (const folder of folders) {
      if (folder.id === targetId) {
        return folder;
      }
      if (folder.subfolder) {
        const found = findFolderRecursively(folder.subfolder, targetId);
        if (found) return found;
      }
    }
    return null;
  };

  const parentFolder = findFolderRecursively(allFolders, folderId);

  if (parentFolder && parentFolder.subfolder) {
    // Recursively collect all subfolder IDs
    const collectSubfolderIds = (subfolders: Folder[]): string[] => {
      const ids: string[] = [];
      for (const subfolder of subfolders) {
        ids.push(subfolder.id);
        if (subfolder.subfolder) {
          ids.push(...collectSubfolderIds(subfolder.subfolder));
        }
      }
      return ids;
    };

    folderIds.push(...collectSubfolderIds(parentFolder.subfolder));
  }

  return folderIds;
}

// Helper function to check if a folder can be deleted (no items or subfolders)
export function canDeleteFolder(folderId: string, gameId: string, itemType: string): { canDelete: boolean; reason?: string } {
  // Check if folder has subfolders
  const allGameFolders = gameFoldersStore.getData();
  const gameData = allGameFolders.find(gf => gf.gameId === gameId);
  
  if (gameData) {
    const folderType = mapItemTypeToFolderType(itemType);
    const section = gameData.folders.find(s => s.type === folderType);
    
    if (section) {
      // Find the folder and check if it has subfolders
      const findFolder = (folders: Folder[], targetId: string): Folder | null => {
        for (const folder of folders) {
          if (folder.id === targetId) {
            return folder;
          }
          if (folder.subfolder) {
            const found = findFolder(folder.subfolder, targetId);
            if (found) return found;
          }
        }
        return null;
      };
      
      const folder = findFolder(section.folders, folderId);
      if (folder && folder.subfolder && folder.subfolder.length > 0) {
        return { canDelete: false, reason: 'Folder contains subfolders. Please delete or move the subfolders first.' };
      }
    }
  }
  
  // Check if folder contains items based on item type
  switch (itemType) {
    case 'documents': {
      const documents = documentStore.getData();
      const documentsInFolder = documents.filter(doc => doc.folder === folderId);
      if (documentsInFolder.length > 0) {
        return { canDelete: false, reason: `Folder contains ${documentsInFolder.length} document(s). Please move or delete the documents first.` };
      }
      break;
    }
    case 'library': {
      const glossaryItems = objectsStore.getData();
      const itemsInFolder = glossaryItems.filter(item => item.folder === folderId);
      if (itemsInFolder.length > 0) {
        return { canDelete: false, reason: `Folder contains ${itemsInFolder.length} library item(s). Please move or delete the items first.` };
      }
      break;
    }
    case 'blueprints':
    case 'templates': {
      const blueprints = templatesStore.getData();
      const blueprintsInFolder = blueprints.filter(blueprint => (blueprint as any).folder === folderId);
      if (blueprintsInFolder.length > 0) {
        return { canDelete: false, reason: `Folder contains ${blueprintsInFolder.length} template(s). Please move or delete the templates first.` };
      }
      break;
    }
  }
  
  return { canDelete: true };
}

// Helper function to convert UI folder structure back to subfolder structure
function convertFromUIFolders(folders: Folder[]): Folder[] {
  return folders.map(folder => ({
    id: folder.id,
    name: folder.name,
    subfolder: folder.children ? convertFromUIFolders(folder.children) : []
  }));
}

// Map item types to game folder types
const mapItemTypeToFolderType = (itemType: string): 'documents' | 'templates' | 'library' | 'dialogs' | 'flows' | 'variables' => {
  switch (itemType) {
    case 'documents':
    case 'document':
      return 'documents';
    case 'blueprints':
    case 'blueprint':
      return 'templates';
    case 'templates':
      return 'templates';
    case 'library':
    case 'glossary':
      return 'library';
    case 'dialogs':
    case 'dialog':
      return 'dialogs';
    case 'flows':
    case 'flow':
      return 'flows';
    case 'variables':
    case 'variable':
      return 'variables';
    default: return 'library';
  }
};

// Store functions that follow the correct pattern (create new arrays instead of mutating)
export const getFolders = (gameId: string, itemType: string): Folder[] => {
  const allGameFolders = gameFoldersStore.getData();
  const gameData = allGameFolders.find(gf => gf.gameId === gameId);

  if (!gameData) return [];

  const folderType = mapItemTypeToFolderType(itemType);
  const section = gameData.folders.find(s => s.type === folderType);

  if (!section) return [];

  return convertToUIFolders(section.folders);
};

/**
 * Get folder name by folder ID
 * @param gameId - The game ID
 * @param itemType - The item type (documents, templates, library)
 * @param folderId - The folder ID to find
 * @returns The folder name or null if not found
 */
export const getFolderName = (gameId: string, itemType: string, folderId: string | null): string | null => {
  if (!folderId) return null;
  
  const allGameFolders = gameFoldersStore.getData();
  const gameData = allGameFolders.find(gf => gf.gameId === gameId);

  if (!gameData) return null;

  const folderType = mapItemTypeToFolderType(itemType);
  const section = gameData.folders.find(s => s.type === folderType);

  if (!section) return null;

  // Helper function to find folder by ID recursively
  const findFolderById = (folders: Folder[], targetId: string): Folder | null => {
    for (const folder of folders) {
      if (folder.id === targetId) {
        return folder;
      }
      if (folder.subfolder) {
        const found = findFolderById(folder.subfolder, targetId);
        if (found) return found;
      }
    }
    return null;
  };

  const folder = findFolderById(section.folders, folderId);
  return folder ? folder.name : null;
};

/**
 * Get folder path as a string like "/MainFolder/SubFolder"
 * @param gameId - The game ID
 * @param itemType - The item type (documents, templates, library)
 * @param folderId - The folder ID to find
 * @returns The folder path string (e.g., "/MainFolder/SubFolder") or empty string if not found
 */
export const getFolderPath = (gameId: string, itemType: string, folderId: string | null | undefined): string => {
  if (!folderId) return '';
  if (!gameId) return '';
  
  const allGameFolders = gameFoldersStore.getData();
  const gameData = allGameFolders.find(gf => gf.gameId === gameId);

  if (!gameData) return '';

  const folderType = mapItemTypeToFolderType(itemType);
  const section = gameData.folders.find(s => s.type === folderType);

  if (!section) return '';

  // Helper function to build path from root to target folder
  const buildPathToFolder = (folders: Folder[], targetId: string, currentPath: string[] = []): string[] | null => {
    for (const folder of folders) {
      const newPath = [...currentPath, folder.name];

      if (folder.id === targetId) {
        return newPath;
      }

      if (folder.subfolder) {
        const result = buildPathToFolder(folder.subfolder, targetId, newPath);
        if (result) return result;
      }
    }
    return null;
  };

  const pathArray = buildPathToFolder(section.folders, folderId);
  if (!pathArray || pathArray.length === 0) return '';
  
  return '/' + pathArray.join('/');
};

/**
 * Get all folder IDs including subfolders for a given parent folder
 * @param gameId - The game ID
 * @param itemType - The item type (documents, templates, library)
 * @param folderId - The parent folder ID
 * @returns Array of folder IDs including the parent and all subfolders
 */
export const getAllSubfolderIds = (gameId: string, itemType: string, folderId: string): string[] => {
  const allGameFolders = gameFoldersStore.getData();
  const gameData = allGameFolders.find(gf => gf.gameId === gameId);

  if (!gameData) return [folderId];

  const folderType = mapItemTypeToFolderType(itemType);
  const section = gameData.folders.find(s => s.type === folderType);

  if (!section) return [folderId];

  return collectAllSubfolderIds(folderId, section.folders);
};

/**
 * Build breadcrumb path for an item's folder, excluding the current selected folder and above
 * @param gameId - The game ID
 * @param itemType - The item type (documents, templates, library)
 * @param itemFolderId - The folder ID of the item
 * @param selectedFolderId - The currently selected folder ID (null for root)
 * @returns Array of folder names representing the breadcrumb path
 */
export const buildFolderBreadcrumbs = (
  gameId: string,
  itemType: string,
  itemFolderId: string | null | undefined,
  selectedFolderId: string | null
): string[] => {
  // If item has no folder or is in root, return empty breadcrumbs
  if (!itemFolderId) return [];

  const allGameFolders = gameFoldersStore.getData();
  const gameData = allGameFolders.find(gf => gf.gameId === gameId);

  if (!gameData) return [];

  const folderType = mapItemTypeToFolderType(itemType);
  const section = gameData.folders.find(s => s.type === folderType);

  if (!section) return [];

  // Helper function to find folder by ID in the hierarchy
  const findFolderById = (folders: Folder[], targetId: string): Folder | null => {
    for (const folder of folders) {
      if (folder.id === targetId) {
        return folder;
      }
      if (folder.subfolder) {
        const found = findFolderById(folder.subfolder, targetId);
        if (found) return found;
      }
    }
    return null;
  };

  // Helper function to build path from root to target folder
  const buildPathToFolder = (folders: Folder[], targetId: string, currentPath: string[] = []): string[] | null => {
    for (const folder of folders) {
      const newPath = [...currentPath, folder.name];

      if (folder.id === targetId) {
        return newPath;
      }

      if (folder.subfolder) {
        const result = buildPathToFolder(folder.subfolder, targetId, newPath);
        if (result) return result;
      }
    }
    return null;
  };

  // Build full path to the item's folder
  const fullPath = buildPathToFolder(section.folders, itemFolderId);
  if (!fullPath) return [];

  // If no folder is selected (showing all items), return the full path
  if (!selectedFolderId) {
    return fullPath;
  }

  // Find the index of the selected folder in the full path
  const selectedFolderPath = buildPathToFolder(section.folders, selectedFolderId);
  if (!selectedFolderPath) return fullPath;

  // Find where the selected folder path intersects with the item's path
  let intersectionIndex = -1;
  for (let i = 0; i < Math.min(fullPath.length, selectedFolderPath.length); i++) {
    if (fullPath[i] === selectedFolderPath[i]) {
      intersectionIndex = i;
    } else {
      break;
    }
  }

  // Check if the item is in a direct subfolder of the selected folder
  const isDirectSubfolder = intersectionIndex === selectedFolderPath.length - 1 &&
    fullPath.length > selectedFolderPath.length;

  if (isDirectSubfolder) {
    // Item is in a direct subfolder - show the remaining path
    const startIndex = intersectionIndex + 1;
    return fullPath.slice(startIndex);
  } else if (intersectionIndex >= 0) {
    // Item shares some common path but is not a direct subfolder
    // Show "..." followed by the last part of the path
    const remainingPath = fullPath.slice(intersectionIndex + 1);
    return remainingPath.length > 0 ? ['...', ...remainingPath] : ['...'];
  } else {
    // No common path - item is in completely different branch
    return ['...', ...fullPath];
  }
};

export const loadFolders = async (gameId: string, itemType: string, gameData?: any): Promise<Folder[]> => {
  try {
    // Add a small delay to ensure loading state is visible
    await new Promise(resolve => setTimeout(resolve, 200));
    
    if (!gameData) {
      throw new Error('Game data is required. Folders should be loaded from user-context endpoint.');
    }

    const game = gameData;

    // Get folders from the game data - it should be an array of GameFolderSection
    let folders = game.folders as GameFolderSection[] || [];

    // If no folders exist, create default structure
    if (folders.length === 0) {
      const defaultFolders: GameFolderSection[] = [
        {
          type: 'documents',
          folders: []
        },
        {
          type: 'templates',
          folders: []
        },
        {
          type: 'library',
          folders: []
        },
        {
          type: 'dialogs',
          folders: []
        }
      ];

      // Try to save the default structure to the game
      try {
        await apiService.updateGame(gameId, {
          folders: defaultFolders
        });
        folders = defaultFolders;
      } catch (saveError) {
        console.warn('Could not save default folders:', saveError);
        // Use the default folders locally anyway
        folders = defaultFolders;
      }
    }

    // Store the folder data
    const currentData = gameFoldersStore.getData();
    const otherGameData = currentData.filter(gf => gf.gameId !== gameId);

    const gameFolder: GameFolders = {
      gameId,
      folders: folders
    };

    const updatedData = [...otherGameData, gameFolder];
    gameFoldersStore.setData(updatedData);

    const result = getFolders(gameId, itemType);

    return result;
  } catch (error) {
    console.error('Error loading folders:', error);
    throw error;
  }
};

export const createFolder = async (
  folderData: FolderCreateRequest,
  toast?: (props: { title?: React.ReactNode; description?: React.ReactNode; variant?: string }) => void
): Promise<Folder> => {
  try {
    const newFolder: Folder = {
      id: Date.now().toString(), // Generate simple ID
      name: folderData.name,
      subfolder: []
    };

    // Update local store with immutable updates
    const currentData = gameFoldersStore.getData();
    const gameDataIndex = currentData.findIndex(gf => gf.gameId === folderData.gameId);

    if (gameDataIndex !== -1) {
      const gameData = currentData[gameDataIndex];
      const folderType = mapItemTypeToFolderType(folderData.itemType);
      
      // Create a deep copy of the game data
      const updatedGameData = JSON.parse(JSON.stringify(gameData));
      let section = updatedGameData.folders.find((s: any) => s.type === folderType);

      if (!section) {
        section = { type: folderType, folders: [] };
        updatedGameData.folders.push(section);
      }

      if (folderData.parentId) {
        // Add to subfolder - recursively find and update
        const addToSubfolder = (folders: Folder[]): boolean => {
          for (let i = 0; i < folders.length; i++) {
            if (folders[i].id === folderData.parentId) {
              if (!folders[i].subfolder) folders[i].subfolder = [];
              folders[i].subfolder!.push(newFolder);
              return true;
            }
            if (folders[i].subfolder && addToSubfolder(folders[i].subfolder)) {
              return true;
            }
          }
          return false;
        };
        addToSubfolder(section.folders);
      } else {
        // Add to root
        section.folders.push(newFolder);
      }

      // Update game via API
      const updateResult = await apiService.updateGame(folderData.gameId, {
        folders: updatedGameData.folders
      });

      if (!updateResult.success) {
        throw new Error(updateResult.error || 'Failed to update game');
      }

      // Create new array with updated game data
      const newData = [...currentData];
      newData[gameDataIndex] = updatedGameData;
      
      // Update local store immediately with new array
      gameFoldersStore.setData(newData);
      
      // Refresh user context to update the game data in unifiedUserStore
      await fetchUserContext();
    }

    if (toast) {
      toast({ title: 'Folder created successfully' });
    }

    return newFolder;
  } catch (error) {
    console.error('Error creating folder:', error);
    if (toast) {
      toast({ 
        title: 'Failed to create folder',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive' 
      });
    }
    throw error;
  }
};

export const updateFolder = async (
  folderData: FolderUpdateRequest,
  toast?: (props: { title?: React.ReactNode; description?: React.ReactNode; variant?: string }) => void
): Promise<Folder> => {
  try {
    const currentData = gameFoldersStore.getData();
    // Create a deep copy to avoid mutating the original data
    const newData = JSON.parse(JSON.stringify(currentData));
    const gameData = newData.find(gf => gf.gameId === folderData.gameId);
    
    if (!gameData) throw new Error('Game not found');

    const folderType = mapItemTypeToFolderType(folderData.itemType);
    const section = gameData.folders.find(s => s.type === folderType);

    if (!section) throw new Error('Section not found');

    let updatedFolder: Folder | null = null;

    // Check if we're moving the folder (parentId is explicitly provided in the request)
    if (folderData.parentId !== undefined) {
      // Prevent moving a folder into itself or its descendants
      if (folderData.parentId === folderData.id) {
        throw new Error('Cannot move a folder into itself');
      }

      // Check if target parent is a descendant of the folder being moved
      const isDescendant = (folderId: string, potentialDescendantId: string, folders: Folder[]): boolean => {
        for (const folder of folders) {
          if (folder.id === folderId) {
            // Found the folder, now check its descendants
            const checkDescendants = (subfolders: Folder[]): boolean => {
              for (const subfolder of subfolders) {
                if (subfolder.id === potentialDescendantId) return true;
                if (subfolder.subfolder && checkDescendants(subfolder.subfolder)) return true;
              }
              return false;
            };
            return folder.subfolder ? checkDescendants(folder.subfolder) : false;
          }
          if (folder.subfolder && isDescendant(folderId, potentialDescendantId, folder.subfolder)) {
            return true;
          }
        }
        return false;
      };

      if (folderData.parentId && isDescendant(folderData.id, folderData.parentId, section.folders)) {
        throw new Error('Cannot move a folder into its own descendant');
      }

      // Step 1: Find and extract the folder from its current location
      let extractedFolder: Folder | null = null;
      
      const extractFromFolders = (folders: Folder[], parent?: Folder): boolean => {
        for (let i = 0; i < folders.length; i++) {
          const folder = folders[i];
          if (folder.id === folderData.id) {
            // Found it! Extract it
            extractedFolder = folders.splice(i, 1)[0];
            // Update its properties
            if (folderData.name) extractedFolder.name = folderData.name;
            if (folderData.color !== undefined) extractedFolder.color = folderData.color || undefined;
            return true;
          }
          if (folder.subfolder && extractFromFolders(folder.subfolder, folder)) {
            return true;
          }
        }
        return false;
      };

      extractFromFolders(section.folders);
      
      if (!extractedFolder) throw new Error('Folder not found');

      // Step 2: Insert the folder into its new location
      if (folderData.parentId === null) {
        // Move to root level
        section.folders.push(extractedFolder);
      } else {
        // Move to a parent folder
        const addToParent = (folders: Folder[]): boolean => {
          for (const folder of folders) {
            if (folder.id === folderData.parentId) {
              if (!folder.subfolder) folder.subfolder = [];
              folder.subfolder.push(extractedFolder!);
              return true;
            }
            if (folder.subfolder && addToParent(folder.subfolder)) {
              return true;
            }
          }
          return false;
        };

        if (!addToParent(section.folders)) {
          throw new Error('Parent folder not found');
        }
      }

      updatedFolder = extractedFolder;
    } else {
      // Just updating folder properties (name, color) without moving
      const updateInFolders = (folders: Folder[]): boolean => {
        for (const folder of folders) {
          if (folder.id === folderData.id) {
            if (folderData.name) folder.name = folderData.name;
            if (folderData.color !== undefined) folder.color = folderData.color || undefined;
            updatedFolder = folder;
            return true;
          }
          if (folder.subfolder && updateInFolders(folder.subfolder)) {
            return true;
          }
        }
        return false;
      };

      updateInFolders(section.folders);

      if (!updatedFolder) throw new Error('Folder not found');
    }

    // Update game via API
    const updateResult = await apiService.updateGame(folderData.gameId, {
      folders: gameData.folders
    });
    
    if (!updateResult.success) {
      throw new Error(updateResult.error || 'Failed to update game');
    }
    
    // Update local store immediately with the new data
    gameFoldersStore.setData(newData);
    
    // Refresh user context to update the game data in unifiedUserStore
    await fetchUserContext();

    if (toast) {
      toast({ title: 'Folder updated successfully' });
    }

    return updatedFolder;
  } catch (error) {
    console.error('Error updating folder:', error);
    if (toast) {
      toast({ 
        title: 'Failed to update folder',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive' 
      });
    }
    throw error;
  }
};

export const deleteFolder = async (
  folderId: string, 
  gameId: string, 
  itemType: string,
  toast?: (props: { title?: React.ReactNode; description?: React.ReactNode; variant?: string }) => void
): Promise<void> => {
  try {
    // Check if folder can be deleted
    const validation = canDeleteFolder(folderId, gameId, itemType);
    if (!validation.canDelete) {
      if (toast) {
        toast({ 
          title: 'Cannot delete folder',
          description: validation.reason || 'Cannot delete folder',
          variant: 'destructive' 
        });
      }
      throw new Error(validation.reason);
    }

    const currentData = gameFoldersStore.getData();
    const gameDataIndex = currentData.findIndex(gf => gf.gameId === gameId);

    if (gameDataIndex === -1) throw new Error('Game not found');

    // Create a deep copy to avoid mutating the original data
    const newData = JSON.parse(JSON.stringify(currentData));
    const gameData = newData[gameDataIndex];
    
    const folderType = mapItemTypeToFolderType(itemType);
    const section = gameData.folders.find((s: any) => s.type === folderType);

    if (!section) throw new Error('Section not found');

    // Remove folder recursively - returns new array without the folder
    const removeFromFolders = (folders: Folder[]): { folders: Folder[], removed: boolean } => {
      const newFolders: Folder[] = [];
      let removed = false;
      
      for (const folder of folders) {
        if (folder.id === folderId) {
          removed = true;
          // Skip this folder (don't add it to newFolders)
        } else {
          if (folder.subfolder) {
            const result = removeFromFolders(folder.subfolder);
            newFolders.push({
              ...folder,
              subfolder: result.folders
            });
            if (result.removed) removed = true;
          } else {
            newFolders.push(folder);
          }
        }
      }
      
      return { folders: newFolders, removed };
    };

    const result = removeFromFolders(section.folders);
    
    if (!result.removed) throw new Error('Folder not found');
    
    // Update the section with the new folders array
    section.folders = result.folders;

    // Update game via API
    const updateResult = await apiService.updateGame(gameId, {
      folders: gameData.folders
    });

    if (!updateResult.success) {
      throw new Error(updateResult.error || 'Failed to update game');
    }

    // Update local store immediately with new data
    gameFoldersStore.setData(newData);
    
    // Refresh user context to update the game data in unifiedUserStore
    await fetchUserContext();

    if (toast) {
      toast({ title: 'Folder deleted successfully' });
    }
  } catch (error) {
    console.error('Error deleting folder:', error);
    if (toast) {
      toast({ 
        title: 'Failed to delete folder',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive' 
      });
    }
    throw error;
  }
};

// Export the store for direct access if needed
export { gameFoldersStore };

// Subscribe function for components
export const subscribeFolders = (callback: (folders: GameFolders[]) => void) => {
  return gameFoldersStore.subscribe(callback);
};
